/**
 * @fileoverview Conflict-check brief formatting, shared by server and client
 * (MOR-992 / MOR-993)
 *
 * Purpose: the conflict-check description is stored as the editable plain text
 * the practitioner approved (`buildConflictBrief` pre-fills it as labelled
 * "Field: value" lines). Rendering it as one pre-wrap block made the check a
 * "giant plain box" — this module parses that text back into structure so the
 * vote email, the vote page and the in-app panels can all lay it out as the
 * same labelled card, while text a practitioner rewrote by hand degrades
 * gracefully to paragraphs.
 *
 * Pure module — no server imports — so the frontend imports it directly
 * (mirrors `@cvx/lib/companyName` et al.).
 *
 * Used by:
 *  - convex/preAppointment/conflictEmail.ts (vote email HTML)
 *  - src/components/pre-appointment/conflict-brief-view.tsx (in-app + vote page)
 *  - src/components/pre-appointment/conflict-party-schedule.tsx (labels)
 */

import type {
  ConflictPartyRegistration,
  ConflictScheduledParty,
} from "@cvx/preAppointment/conflictChecks/schema";

// ---------------------------------------------------------------------------
// Brief text → blocks
// ---------------------------------------------------------------------------

export type ConflictBriefBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "fields"; fields: Array<{ label: string; value: string }> };

/**
 * A labelled brief line: "Registered office: Level 11, Melbourne". The colon
 * must be followed by whitespace so URLs ("https://…") and times stay prose,
 * and the label is kept short so a sentence containing a colon mid-way
 * through doesn't masquerade as a field.
 */
const FIELD_LINE = /^([^:\n]{1,48}):\s+(.+)$/;

/**
 * Parse a conflict-check description into renderable blocks: consecutive
 * "Label: value" lines group into one fields block; everything else stays a
 * paragraph (blank lines separate them). A description written free-hand
 * comes back as paragraphs only — the caller renders those exactly as the
 * old pre-wrap block did.
 */
export function parseConflictBrief(description: string): ConflictBriefBlock[] {
  const blocks: ConflictBriefBlock[] = [];
  let fields: Array<{ label: string; value: string }> | null = null;
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", text: paragraph.join("\n") });
      paragraph = [];
    }
  };
  const flushFields = () => {
    if (fields) {
      blocks.push({ kind: "fields", fields });
      fields = null;
    }
  };

  for (const rawLine of description.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushFields();
      continue;
    }
    const match = FIELD_LINE.exec(line);
    if (match) {
      flushParagraph();
      (fields ??= []).push({ label: match[1].trim(), value: match[2].trim() });
    } else {
      flushFields();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushFields();
  return blocks;
}

// ---------------------------------------------------------------------------
// Entity group naming (MOR-1419)
// ---------------------------------------------------------------------------

/**
 * Entities named in full before the group name collapses to "A and N other
 * entities" — three long company names already make an email subject.
 */
const MAX_NAMED_ENTITIES = 3;

/**
 * One name for the entities a check covers, for the email subject and
 * heading, the vote page and the schedule filename: the entity itself for a
 * single-entity check; "A and B" / "A, B and C" for a small group; and
 * "A and N other entities" beyond that, where the brief lists them all.
 */
export function formatEntityGroupName(names: string[]): string {
  const [first, ...rest] = names;
  if (first === undefined) return "";
  if (rest.length === 0) return first;
  if (names.length > MAX_NAMED_ENTITIES) {
    return `${first} and ${rest.length} other entities`;
  }
  const last = rest[rest.length - 1] ?? "";
  const middle = rest.slice(0, -1);
  return `${[first, ...middle].join(", ")} and ${last}`;
}

// ---------------------------------------------------------------------------
// PPSR registration display
// ---------------------------------------------------------------------------

/** Human labels for the PPSR collateral classes the SI agent records. */
export const COLLATERAL_CLASS_LABELS: Record<string, string> = {
  motor_vehicle: "Motor vehicle",
  allpap_no_exceptions: "ALLPAP (no exceptions)",
  allpap_with_exceptions: "ALLPAP (with exceptions)",
  other_goods: "Other goods",
  account: "Account",
  intangible_property: "Intangible property",
  financial_property: "Financial property",
  chattel_paper: "Chattel paper",
  general_intangible: "General intangible",
  inventory: "Inventory",
  negotiable_instrument: "Negotiable instrument",
};

export function collateralClassLabel(value: string | undefined): string | null {
  if (!value) return null;
  return COLLATERAL_CLASS_LABELS[value] ?? value;
}

/**
 * Compact labels for the narrow schedule table — "ALLPAP" is the PPSR term
 * practitioners use, and the full phrase truncated mid-word in a side panel.
 */
const COLLATERAL_CLASS_SHORT_LABELS: Record<string, string> = {
  allpap_no_exceptions: "ALLPAP",
  allpap_with_exceptions: "ALLPAP (exceptions)",
};

export function collateralClassShortLabel(
  value: string | undefined,
): string | null {
  if (!value) return null;
  return COLLATERAL_CLASS_SHORT_LABELS[value] ?? collateralClassLabel(value);
}

/** ISO date → AU display ("12/03/2024"). Anything else passes through. */
export function formatAuDateIso(iso: string | undefined): string | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!match) return iso.trim() || null;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

/**
 * The secondary line for one registration: collateral class, registration
 * number and start date, dot-separated — the PMSI/general distinction is a
 * badge, not part of this line.
 */
export function registrationDetailLine(
  registration: ConflictPartyRegistration,
): string {
  const parts = [
    collateralClassLabel(registration.collateralClass),
    registration.registrationNumber
      ? `Reg ${registration.registrationNumber}`
      : null,
    formatAuDateIso(registration.startDate),
  ].filter((part): part is string => part !== null);
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Party schedule rows
// ---------------------------------------------------------------------------

/**
 * One schedule-table row: a registration, or a bare party when it has none.
 * The party's name prints on its first row only, so a party holding several
 * registrations reads as one block rather than a repeated name.
 */
export interface ScheduleRow {
  name: string;
  /** False on the second and later registrations of the same party. */
  firstOfParty: boolean;
  registration: ConflictPartyRegistration | null;
}

export function flattenScheduleRows(
  parties: ConflictScheduledParty[],
): ScheduleRow[] {
  return parties.flatMap((party): ScheduleRow[] => {
    const registrations = party.registrations ?? [];
    if (registrations.length === 0) {
      return [{ name: party.name, firstOfParty: true, registration: null }];
    }
    return registrations.map((registration, index) => ({
      name: party.name,
      firstOfParty: index === 0,
      registration,
    }));
  });
}

/** Comma-separated values with at least this many items are treated as lists. */
export const LIST_MIN_ITEMS = 5;

/**
 * Split a brief value into list items when it reads as a list (trading
 * names, a long run of directors); null for prose and short values, which
 * render as-is. Addresses stay prose: "40 Proctors Road, Richmond, VIC,
 * 3121" is under the threshold.
 */
export function briefValueItems(value: string): string[] | null {
  const items = value.split(/,\s+/).map((item) => item.trim());
  return items.length >= LIST_MIN_ITEMS ? items : null;
}
